import React from 'react';
import { Setting } from '../../types/settings.types';
import { LedgerSkeleton } from '../shell/LedgerSkeleton';
import SettingField from './SettingField';

interface SettingsTabProps {
  settings: Setting[];
  values: Record<string, any>;
  onChange: (key: string, value: any) => void;
  errors: Record<string, string>;
  disabled?: boolean;
  loading?: boolean;
}

const SettingsTab: React.FC<SettingsTabProps> = ({
  settings,
  values,
  onChange,
  errors,
  disabled = false,
  loading = false,
}) => {
  // Group settings by ui_group
  const groupedSettings = settings.reduce((acc, setting) => {
    const group = setting.ui_group || 'Other';
    if (!acc[group]) {
      acc[group] = [];
    }
    acc[group].push(setting);
    return acc;
  }, {} as Record<string, Setting[]>);

  // Sort settings within each group by ui_order
  Object.keys(groupedSettings).forEach((group) => {
    groupedSettings[group].sort((a, b) => a.ui_order - b.ui_order);
  });

  if (loading) {
    return <LedgerSkeleton lines={6} className="mt-4" />;
  }

  if (settings.length === 0) {
    return <p className="py-6 font-serif text-scale-1 italic text-ink-muted">No settings available in this category.</p>;
  }

  return (
    <>
      {Object.entries(groupedSettings).map(([groupName, groupSettings]) => (
        <section key={groupName} aria-label={groupName}>
          <div className="cap-label mt-4 border-b border-ink pb-0.5">{groupName}</div>
          {groupSettings.map((setting) => (
            <SettingField
              key={setting.key}
              setting={setting}
              value={values[setting.key] ?? setting.default_value}
              onChange={onChange}
              error={errors[setting.key]}
              disabled={disabled}
            />
          ))}
        </section>
      ))}
    </>
  );
};

export default SettingsTab;
